import { useTranslation, untranslated } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent/index";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useRef } from "react";
import Forms, { type FormTemplateRow } from "./index.js";
import { showToastNotification } from "@Reducers/toastSlice";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants.js";
import { projectTemplateConfigHandler } from "@Actions/projectSetting.js";
import { useParams } from "react-router-dom";
import Text from "@CubeComponents/html/Text/Text.js";
import { toObjectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";

type ProjectTemplateConfigProps = {
  /** A `formCategories` value — scopes the embedded template grid. */
  formCategory: string;
  /** `formTemplateSource.portalSettings` | `formTemplateSource.projectSettings`. */
  source?: string;
  setShowTemplatesGrid: (show: boolean) => void;
  refreshGrid: () => void;
  /** Ids already attached to this project; forwarded by the host but not read here. */
  excludeTemplates?: string[];
};

function ProjectTemplateConfig({
  formCategory,
  source,
  setShowTemplatesGrid,
  refreshGrid,
}: ProjectTemplateConfigProps) {
  const { t } = useTranslation();
  const dataGrid = useRef<SlickGrid1Ref<FormTemplateRow> | null>(null);
  const dispatch = useDispatch();
  // Route params enter from outside the type system; the backend route schema validates this id.
  const params = useParams<{ projectId: ObjectIdString }>();

  const handleAddTemplate = async () => {
    const selectedRecords = dataGrid.current!.dataView.getAllSelectedItems();
    if (selectedRecords.length && params.projectId) {
      // Every row the grid hands back came from Mongo with an `_id`.
      const templateIds = selectedRecords.map((obj) => toObjectIdString(obj._id!));
      const res = await projectTemplateConfigHandler({
        action: "add" as const,
        templateIds,
        projectId: params.projectId,
      });

      setShowTemplatesGrid(false);
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Template(s) added successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        refreshGrid();
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } else {
      dispatch(
        showToastNotification({
          content: t("Select template first to add them to this project"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  return (
    <ModalStateDialogComponent
      allowModalPadding={false}
      isVisible
      height={"80%"}
      onCancel={() => setShowTemplatesGrid(false)}
      headerTitle={<Text className=" w-full justify-between flex">{t("Select Template")}</Text>}
      footerContent={
        <div className="">
          <ButtonComponent onClick={handleAddTemplate} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <Text>{t("Add")}</Text>
          </ButtonComponent>
        </div>
      }
      modelContent={
        <Forms
          excludeTemplates={true}
          source={source}
          ref={dataGrid}
          formCategory={formCategory}
          showOnlyGrid
          projectId={params.projectId}
        />
      }
      width="80%"
    />
  );
}
export default ProjectTemplateConfig;
